import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { useDarkMode } from './DarkModeContext';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { setIsDarkMode } = useDarkMode();

  // Check if user is authenticated on app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      console.log('🔄 UserContext: Loading user data...');
      setLoading(true);
      const response = await api.getCurrentUser();
      console.log('📊 UserContext: Received user data:', response.data.user);
      setUser(response.data.user);
      setError(null);
      console.log('✅ UserContext: User data updated successfully');
    } catch (err) {
      console.error('❌ UserContext: Failed to load user:', err);
      logout(); // Clear invalid token
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.register(userData);
      
      // Store token and user data
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.login(credentials);
      
      // Store token and user data
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setError(null);
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.updateProfile(profileData);
      setUser(response.data.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      return await api.forgotPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      setError(null);
      return await api.resetPassword(token, newPassword);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const verifyEmail = async (token) => {
    try {
      setError(null);
      const response = await api.verifyEmail(token);
      if (user) {
        setUser({ ...user, emailVerified: true });
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setError(null);
      return await api.resendVerificationEmail();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Social login methods - using server-side OAuth flow

  const facebookLogin = async (token) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.facebookLogin(token);
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Legacy methods for backward compatibility
  const addXP = async (amount) => {
    try {
      const response = await api.updateUserStats({ xpGained: amount });
      setUser(response.data.user);
      return response;
    } catch (err) {
      console.error('Error updating XP:', err);
      // Fallback to local update if backend fails
      if (user) {
        setUser(prev => ({
          ...prev,
          xp: (prev.xp || 0) + amount,
          level: Math.floor(((prev.xp || 0) + amount) / 1000) + 1
        }));
      }
    }
  };

  const updateModuleProgress = async (moduleId, score, timeSpent) => {
    try {
      console.log('🔄 Updating module progress:', { moduleId, score, timeSpent });
      const response = await api.completeModule({ moduleId, score, timeSpent });
      console.log('✅ Module progress updated successfully:', response.data);
      setUser(response.data.user);
      return response;
    } catch (err) {
      console.error('❌ Error updating module progress:', err);
      // Fallback to local update if backend fails
      if (user) {
        console.log('🔄 Falling back to local update');
        setUser(prev => ({
          ...prev,
          moduleProgress: {
            ...prev.moduleProgress,
            [moduleId]: {
              score,
              timeSpent: (prev.moduleProgress?.[moduleId]?.timeSpent || 0) + timeSpent,
              completed: score >= 70
            }
          },
          completedModules: score >= 70 
            ? [...new Set([...(prev.completedModules || []), moduleId])]
            : (prev.completedModules || []),
          totalTimeSpent: (prev.totalTimeSpent || 0) + timeSpent
        }));
      }
    }
  };

  const updatePreferences = async (newPreferences) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.updatePreferences(newPreferences);
      setUser(response.data.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (passwordData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.updatePassword(passwordData);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateProfileImage = async (imageData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.updateProfileImage(imageData);
      setUser(response.data.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getTotalModuleXP = () => {
    // Calculate total XP from completed modules
    const moduleXPRewards = {
      1: 200, 2: 300, 3: 400, 4: 350, 5: 450, 
      6: 500, 7: 350, 8: 400, 9: 200, 10: 400
    };
    
    return (user?.completedModules || []).reduce((total, moduleId) => {
      return total + (moduleXPRewards[moduleId] || 0);
    }, 0);
  };

  const getCompletionRate = () => {
    const totalModules = 10;
    return Math.round(((user?.completedModules?.length || 0) / totalModules) * 100);
  };

  // Sync dark mode with user preferences
  useEffect(() => {
    if (user && user.preferences && user.preferences.theme) {
      if (user.preferences.theme === 'dark') setIsDarkMode(true);
      else if (user.preferences.theme === 'light') setIsDarkMode(false);
      else if (user.preferences.theme === 'auto') setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
  }, [user, setIsDarkMode]);

  const value = {
    // Authentication state
    user,
    loading,
    error,
    isAuthenticated: !!user,
    
    // State setters
    setUser,
    loadUser,
    
    // Authentication methods
    register,
    login,
    logout,
    updateProfile,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
    facebookLogin,
    
    // Legacy methods for backward compatibility
    userData: user, // Alias for backward compatibility
    addXP,
    updateModuleProgress,
    updatePreferences,
    updatePassword,
    updateProfileImage,
    getTotalModuleXP,
    getCompletionRate
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 